import React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { ApplicationState } from './store';
import { history } from './AppHistory';
import App from './App';
import configureStore from './store/configureStore';
import registerServiceWorker from './registerServiceWorker';
import { actionCreators as ServiceWorkerStoreActionCreators } from './store/ServiceWorkerStore';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { colors } from './colors';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    flat: true;
  }
}

const customTheme = createTheme({
  typography: {
    fontFamily: ['Open Sans', 'sans-serif'].join(','),
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    body1: {
      fontSize: '0.875rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
    h5: {
      fontSize: '1.5rem',
    },
    h6: {
      fontSize: '1.5rem',
    }
  },
  components: {
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '35px',
          color: 'black'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: "white"
          }
        }
      }
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          margin: 10,
          color: colors.white,
          textTransform: 'none',
        },
      },
      variants: [
        {
          props: { variant: "flat" },
          style: {
            color: colors.black,
          }
        }
      ],
    }
  },
  palette : {
    primary : {
      main : colors.primary.bondiblue,
      dark: colors.primary.bluezodiac,
      light: colors.primary.milkyway,
    },
    secondary: {
      main: colors.white,
      dark: colors.black
    },
    error: {
      main: colors.secondary.candy
    },
    background: {
      default: colors.white
    }
  }
});

// Get the application-wide store instance, prepopulating with state from the server where available.
const initialState = (window as any).initialReduxState as ApplicationState;
const { store, persistor } = configureStore(initialState);

export type RootState = ReturnType<typeof store.getState>;

const rootElement = document.getElementById('root');

const onUpdate = () => {
  store.dispatch(ServiceWorkerStoreActionCreators.updateServiceWorker());
};

ReactDOM.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <Router history={history}>
        <ThemeProvider theme={customTheme}>
          <App />
        </ThemeProvider>
      </Router>
    </PersistGate>
  </Provider>,
  rootElement
);

registerServiceWorker(onUpdate);